import { NonFieldError } from "@/components/common/SCAlert";
import { getProjectParticipationInfo } from "@/components/examinee/participation/ParticipationAPI";
import { ParticipationUrlInfo } from "@/components/examinee/participation/Types";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useParams } from "react-router-dom";

/**
 * プロジェクト固有 URL（/examinee/greetings/p/:urlId）のランディングページ。
 * アカウント登録とログイン参加の両方が可能（受付期間の制御はなく常時アクセス可）。
 */
export const ProjectGreetings = () => {
  const { urlId } = useParams();
  const [info, setInfo] = useState<ParticipationUrlInfo | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!urlId) {
      setNotFound(true);
      return;
    }
    getProjectParticipationInfo(urlId)
      .then((res) => setInfo(res.data))
      .catch(() => setNotFound(true));
  }, [urlId]);

  if (notFound) {
    return <InvalidUrl />;
  }

  if (info == null) {
    return (
      <Stack alignItems="center" sx={{ py: 20 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const joinPath = `/examinee/project_join?url_id=${urlId}`;

  return (
    <Stack alignItems="center" sx={{ width: "100%" }}>
      <Stack sx={{ pt: 12, px: 6, pb: 9, gap: 6, width: "100%" }}>
        <Typography
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: "700",
            fontSize: "34px",
            lineHeight: "130%",
            letterSpacing: "0.25px",
            textAlign: "center",
            color: "primary.main",
          }}
        >
          睡眠健康測定を
          <br />
          はじめましょう
        </Typography>
        <ProjectNameCard info={info} />
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: "400",
            fontSize: "16px",
            lineHeight: "175%",
            letterSpacing: "0.15px",
            textAlign: "center",
            color: "text.primary",
          }}
        >
          アカウントの登録（またはログイン）を行い、測定に参加してください。
        </Typography>
      </Stack>
      <Stack sx={{ width: "100%", px: 6, pb: 20, gap: 4 }}>
        <Button
          component={RouterLink}
          to="/examinee/terms_of_service_agreement"
          variant="contained"
          size="large"
          state={`/examinee/project_signup?url_id=${urlId}`}
          sx={{ boxShadow: "none", width: "100%" }}
        >
          はじめてご利用の方
        </Button>
        <Stack sx={{ gap: 2 }}>
          <Button
            component={RouterLink}
            to={joinPath}
            variant="outlined"
            size="large"
            sx={{ boxShadow: "none", width: "100%" }}
          >
            既にアカウントをお持ちの方
          </Button>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "175%",
              letterSpacing: "0.4px",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            ※SLEEP COMPASS Lightと共通のアカウントでご利用いただけます。
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export function ProjectNameCard({ info }: { info: ParticipationUrlInfo }) {
  if (!info.organization_name && !info.project_name) {
    return null;
  }
  return (
    <Stack
      sx={{
        gap: 1,
        borderRadius: "8px",
        p: 4,
        background: "#0056A00A",
        textAlign: "center",
      }}
    >
      {info.organization_name && (
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          {info.organization_name}
        </Typography>
      )}
      {info.project_name && (
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {info.project_name}
        </Typography>
      )}
    </Stack>
  );
}

export function InvalidUrl() {
  return (
    <Stack spacing={6} sx={{ mt: 12, mx: 4, mb: 16 }}>
      <NonFieldError>
        URLが正しくありません。配布されたURLをもう一度ご確認ください。
      </NonFieldError>
    </Stack>
  );
}
